import { Box, Typography } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AttributeGroupPreview from '../attributes/AttributeGroupPreview';
import { Field, Type, getAttributesByGroups, getObjectSortedByKeys } from '../attributes/attributes';
import FieldSelector from '../attributes/FieldSelector';
import type { QuickFilter } from '../common/components/filtering/QuickFilterItem';
import QuickFilterList from '../common/components/filtering/QuickFilterList';
import I18n from '../common/i18n/I18n';
import { H, V } from '../common/Layout';

type FieldDefinitionCarouselProps = {
    types: Type[];
    selectedFields: Field[];
    setSelectedType: React.Dispatch<React.SetStateAction<any>>;
    typeClicked: Type;
    setTypeClicked: React.Dispatch<React.SetStateAction<any>>;
    fields: Field[];
    setSelectedFields: React.Dispatch<React.SetStateAction<any>>;
    isInModal?: boolean;
};

export function FieldDefinitionCarousel(props: FieldDefinitionCarouselProps) {
    const {
        types,
        selectedFields,
        setSelectedType,
        typeClicked,
        setTypeClicked,
        fields,
        setSelectedFields,
        isInModal
    } = props;

    const attributesByGroups = useMemo(() => {
        return getObjectSortedByKeys(getAttributesByGroups(fields));
    }, [fields]);

    const [filteredAttributesByGroups, setFilteredAttributesByGroups] = useState(attributesByGroups);

    const groupOptions = useMemo(() => Object.keys(attributesByGroups), [attributesByGroups]);

    const [groupFilter, setGroupFilter] = useState<QuickFilter>({
        id: 'group',
        label: 'Field Group',
        displayType: 'SearchMultiSelectFacet',
        options: groupOptions,
        values: []
    });

    useEffect(() => {
        setFilteredAttributesByGroups(attributesByGroups);
        setGroupFilter((prev) => ({ ...prev, options: groupOptions, values: [] }));
    }, [attributesByGroups, groupOptions]);

    const handleFilterChanged = useCallback(
        (updatedFilter: QuickFilter) => {
            const values = updatedFilter.values ?? [];

            if (values.length === 0) {
                setFilteredAttributesByGroups(attributesByGroups);
                return;
            }

            setGroupFilter(updatedFilter);

            const filtered: Record<string, Field[]> = {};
            values.forEach((groupName) => {
                if (attributesByGroups[groupName]) {
                    filtered[groupName] = attributesByGroups[groupName];
                }
            });
            setFilteredAttributesByGroups(filtered);
        },
        [attributesByGroups]
    );

    const handleToggleGroupSelection = useCallback(
        (attributes: Field[], selectAll: boolean) => {
            setSelectedType(undefined);
            setTypeClicked(undefined);
            setSelectedFields((prevVal) => {
                if (selectAll) {
                    const newFields = attributes.filter((attr) => !prevVal.some((f) => f.id === attr.id));
                    return [...prevVal, ...newFields];
                } else {
                    const attributeIds = new Set(attributes.map((attr) => attr.id));
                    return prevVal.filter((f) => !attributeIds.has(f.id));
                }
            });
        },
        [setSelectedType, setTypeClicked, setSelectedFields]
    );

    return (
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography fontSize={'large'} sx={{ ml: 1 }}>
                <I18n token="fieldDefinition.modal.addAttributes.subtitle.fieldType" />
            </Typography>
            {!!types.length && (
                <FieldSelector
                    carousel
                    isType={true}
                    isDisabled={false}
                    available={types as Field[]}
                    isSelected={(field) => typeClicked && field.fieldType === typeClicked.fieldType}
                    onClick={(type) => {
                        setTypeClicked({
                            ...type,
                            description: '' // After selecting a type, clear previously selected description as it should be empty initially
                        });
                        setSelectedFields([]);
                    }}
                />
            )}
            <Typography fontSize={'large'} sx={{ mt: 1, ml: 1 }}>
                <I18n token="fieldDefinition.modal.addAttributes.subtitle.existingFields" />
            </Typography>

            {fields.length ? (
                <V gap={2}>
                    {groupOptions.length > 0 && (
                        <QuickFilterList
                            filters={[groupFilter]}
                            onFilterChanged={handleFilterChanged}
                            allowAutoSwitchingQuickFilterType={false}
                            sx={{ width: '300px', ml: 2 }}
                        />
                    )}
                    <H gap={2} sx={{ flexWrap: 'wrap', mx: 2, width: 'unset' }}>
                        {Object.keys(filteredAttributesByGroups).map((key) => {
                            const groupFields = filteredAttributesByGroups[key];
                            const selected = groupFields.some((field) => selectedFields.some((f) => f.id === field.id));
                            return (
                                <V key={key}>
                                    <AttributeGroupPreview
                                        id={'field-definitions-' + key}
                                        group={key}
                                        attributes={groupFields}
                                        isHover={selected}
                                        isSelected={(field) =>
                                            selectedFields.length && selectedFields.map((f) => f.id).includes(field.id)
                                        }
                                        isInModal={isInModal}
                                        disableFlexGrow={isInModal}
                                        onClickAttribute={(field) => {
                                            setSelectedType(undefined);
                                            setTypeClicked(undefined);
                                            setSelectedFields((prevVal) => {
                                                const isFieldSelected = prevVal.some((f) => f.id === field.id);
                                                if (isFieldSelected) {
                                                    return prevVal.filter((f) => f.id !== field.id);
                                                } else {
                                                    return [...prevVal, field];
                                                }
                                            });
                                        }}
                                        onToggleGroupSelection={handleToggleGroupSelection}
                                    />
                                </V>
                            );
                        })}
                    </H>
                </V>
            ) : (
                <H sx={{ alignItems: 'center', justifyContent: 'center', py: 3 }}>
                    <Typography fontSize={'small'}>
                        <I18n token="fieldDefinition.modal.addAttributes.noFields" />
                    </Typography>
                </H>
            )}
        </Box>
    );
}
